/**
 * The web caller waits in the room while it rings, as the app does (plan #71).
 *
 * Before 2026-10-02 the web caller waited on the portal's ring bar and loaded
 * the room only after the answer, which cost Devin's first call about 9 s of
 * its 30 (docs/call-room-2026-09-28.md). Now the portal (client-profile.js
 * handleRinging) records the ring on the stored call (`outgoingRing`) and
 * opens the room at once. The room page never hears that p2p_ringing, so it
 * takes the ring from the stored call (outgoingRing.ts resumeOutgoingRing).
 *
 * How the ring ends goes back to the portal through this tab's
 * sessionStorage, because leaving the room loads the portal afresh:
 * - a decline or the 60 s running out: the call-end notice
 *   (groupEndForAll.ts rememberCallEndNotice);
 * - no answer reported by the server: UNANSWERED_CALL_KEY, which the portal
 *   shows as the same "didn't answer" card as before, Leave a VideoMail
 *   included (client-profile.js showUnansweredCall).
 *
 * Direct calls only. A group call still rings from the portal's ring bar.
 */
/** Read once by the portal, and only while fresh (client-profile.js). */
export const UNANSWERED_CALL_KEY = 'vrs_unanswered_call';
/** The server's report is small; anything larger is not ours to pass on. */
const MAX_UNANSWERED_CALL_BYTES = 8000;
/**
 * This tab's sessionStorage. Web only, as in groupEndForAll.ts: native has no tab.
 *
 * @returns {Storage|undefined}
 */
function sessionStore() {
    try {
        // The native build's globals have no sessionStorage at all.
        const scope = globalThis;
        return scope.sessionStorage;
    }
    catch {
        return undefined;
    }
}
/**
 * Room names compare case-insensitively, as the server writes them.
 *
 * @param {unknown} left - One room name.
 * @param {unknown} right - The other.
 * @returns {boolean}
 */
function sameRoom(left, right) {
    return typeof left === 'string' && typeof right === 'string' && Boolean(left)
        && left.toLowerCase() === right.toLowerCase();
}
/**
 * The ring the portal recorded for the call in this room, when this page
 * should take it over: a direct call, this page's room, a start time.
 *
 * @param {StoredActiveCall|null|undefined} stored - The stored call.
 * @param {string|undefined} roomName - The room this page shows.
 * @returns {IResumableRing|undefined}
 */
export function storedOutgoingRing(stored, roomName) {
    const marker = stored?.outgoingRing;
    if (!stored?.callId || !marker || stored.groupCall === true || !sameRoom(stored.roomName, roomName)
        || typeof marker.startedAt !== 'number' || !Number.isFinite(marker.startedAt)) {
        return undefined;
    }
    return {
        callId: stored.callId,
        ...typeof marker.calleeId === 'string' && marker.calleeId ? { calleeId: marker.calleeId } : {},
        startedAt: marker.startedAt
    };
}
/**
 * The stored call without its ring: once anyone answers, a reload of the room
 * must not take the ring up again (leaving would cancel a live call).
 *
 * @param {StoredActiveCall} stored - The stored call.
 * @returns {StoredActiveCall}
 */
export function withoutOutgoingRing(stored) {
    const rest = { ...stored };
    delete rest.outgoingRing;
    return rest;
}
/**
 * Hand the server's "no answer" report to the portal the room is about to
 * load. Tab-scoped; no token or account id beyond what the server sent this
 * caller for the card.
 *
 * @param {Object} payload - The p2p_target_offline data.
 * @param {number} now - The current time.
 * @returns {void}
 */
export function rememberUnansweredCall(payload, now = Date.now()) {
    let value;
    try {
        value = JSON.stringify({
            at: now,
            payload,
            version: 1
        });
    }
    catch {
        return;
    }
    if (value.length > MAX_UNANSWERED_CALL_BYTES) {
        return;
    }
    try {
        sessionStore()?.setItem(UNANSWERED_CALL_KEY, value);
    }
    catch {
        // Storage blocked: the portal says nothing, as before the room took the ring.
    }
}
